import React, { memo } from "react";
import styled from "styled-components/macro";
import { ProductCartItem } from "./types";
import climaIMG from "assets/images/clima_img.png";
import { MainPrice, SecondaryPrice, smallShadow } from "theme/GlobalElements";
import { device, TabletUp, Mobile } from "theme/Breakpoints";
import CartCounter from "./components/CartCounter";
import { FaTrash } from "react-icons/fa";
import { colors } from "theme/Theme";
import { useInstallation } from "hooks/useInstallation";
import { useNavigate } from "react-router";
import DualPrice, { EuroPrice } from "components/Price";

interface CartElementProps {
  item: ProductCartItem;
  updateItem: (id: string, count: number) => void;
}

const CartElement: React.FC<CartElementProps> = ({ item, updateItem }) => {
  const navigate = useNavigate();

  const removeElement = () => {
    updateItem(item.product._id, 0);
  };
  const { installationPrice, installationPriceEur } = useInstallation(
    item.product.power_btu,
    item.product.type
  );

  const handleClick = () => {
    navigate(
      `/${item.product.objectType}/${encodeURIComponent(item.product.alias)}`
    );
  };
  const image = item.product.images
    ? item.product.images[0] ?? item.product.images
    : item.product.image;
  return (
    <CartElementContainer>
      <DesktopContainer>
        <Navigation onClick={handleClick}>
          <ProductImage
            alt={image.alt ?? ""}
            src={`data:${image.contentType};base64,${image.image}`}
          />
          <TitleAndPrice>
            <Mobile>
              <span className="text-sm">{item.product.title}</span>
              <MobilePricesAndCounter>
                <Prices>
                  {item.product.price_promotion && (
                    <SecondaryPrice className="text-sm">
                      <EuroPrice
                        bgn={item.product.price}
                        eur={item.product.price_eur}
                      />
                    </SecondaryPrice>
                  )}
                  <MainPrice className="text-body">
                    <DualPrice
                      bgn={item.product.price_promotion
                        ? item.product.price_promotion
                        : item.product.price}
                      eur={item.product.price_promotion
                        ? item.product.price_promotion_eur
                        : item.product.price_eur}
                    />
                  </MainPrice>
                </Prices>
                <Prices>
                  <CartCounter
                    itemId={item.product._id}
                    itemCount={item.count ? item.count : 0}
                    updateItem={updateItem}
                  />
                  {item.installation && (
                    <Installation className="text-xs">
                      (Включен монтаж
                      <DualPrice
                        bgn={installationPrice}
                        eur={installationPriceEur}
                      />)
                    </Installation>
                  )}
                </Prices>
              </MobilePricesAndCounter>
            </Mobile>
            <TabletUp>
              <span className="text-body">{item.product.title}</span>
              <MobilePricesAndCounter>
                <Prices>
                  {item.product.price_promotion && (
                    <SecondaryPrice className="text-body">
                      <EuroPrice
                        bgn={item.product.price}
                        eur={item.product.price_eur}
                      />
                    </SecondaryPrice>
                  )}
                  <PricePlus>
                    <MainPrice className="text-md">
                      <DualPrice
                        bgn={item.product.price_promotion
                          ? item.product.price_promotion
                          : item.product.price}
                        eur={item.product.price_promotion
                          ? item.product.price_promotion_eur
                          : item.product.price_eur}
                      />
                    </MainPrice>
                    {item.installation && (
                      <Installation className="text-xs">
                        (Включен монтаж
                        <DualPrice
                          bgn={installationPrice}
                          eur={installationPriceEur}
                        />)
                      </Installation>
                    )}
                  </PricePlus>
                </Prices>
              </MobilePricesAndCounter>
            </TabletUp>
          </TitleAndPrice>
        </Navigation>
        <TabletUp>
          <CartCounter
            itemId={item.product._id}
            itemCount={item.count ? item.count : 0}
            updateItem={updateItem}
          />
        </TabletUp>
      </DesktopContainer>

      <RemoveItem onClick={removeElement}>
        <FaTrash className="filter-remove" />
      </RemoveItem>
    </CartElementContainer>
  );
};

const CartElementContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  padding: 5px;
  ${smallShadow}
  max-width: 1200px;
  width: 90%;
  margin-bottom: 20px;

  & + & {
    margin-top: 25px;
  }
  @media only screen and (${device.tablet}) {
    width: 80%;
  }
`;

const PricePlus = styled.div`
  display: inline-flex;
  align-items: baseline;
  order: 2;
  ${MainPrice} {
    order: 0 !important;
    margin-right: 10px;
  }
`;
const ProductImage = styled.img`
  width: 80px;
  height: 60px;
  object-fit: contain;

  @media only screen and (${device.tablet}) {
    width: 120px;
    height: 90px;
  }

  @media only screen and (${device.laptop}) {
    width: 180px;
    height: 135px;
  }
`;

const Prices = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  margin-top: 5px;

  ${MainPrice} {
    order: 2;
  }

  ${SecondaryPrice} {
    order: 1;
    margin: 0;
  }

  @media only screen and (${device.laptop}) {
    margin-top: 10px;

    ${PricePlus} {
      order: 2;
    }
  }
`;

const DesktopContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-around;
`;

const Navigation = styled.div`
  cursor: pointer;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const TitleAndPrice = styled.div`
  display: flex;
  flex-flow: column;
  align-self: stretch;
  flex: 1;
  margin-left: 10px;

  @media only screen and (${device.laptop}) {
    margin-left: 20px;
  }
`;

const RemoveItem = styled.div`
  cursor: pointer;
  position: absolute;
  top: 5px;
  right: 5px;

  .filter-remove {
    width: 14px;
    height: 14px;
    fill: ${colors.gray};

    @media only screen and (${device.laptop}) {
      width: 18px;
      height: 18px;
    }
  }

  &:hover {
    .filter-remove {
      fill: ${colors.red};
    }
  }
`;

const MobilePricesAndCounter = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
`;

const Installation = styled.p`
  color: ${colors.lighter_blue};
  span {
    font-weight: bold;
  }
`;

export default memo(CartElement);
